// DeviceTableTab.tsx
import { useEffect, useMemo, useState } from "react";
import { Row, Col, Form, Button, Modal } from "react-bootstrap";
import { Calendar } from "react-bootstrap-icons";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import http from "services/httpClient";

interface DeviceLabel {
  column_index: number;
  label: string;
  is_enabled: boolean;
  multiplier?: number;
  offset?: number;
  unit?: string;
}

interface DeviceDataRow {
  timestamp: string;
  values: Record<string, number | null>;
}

interface DeviceTableTabProps {
  deviceId: number;
  deviceName: string;
  labels: DeviceLabel[];
  labelMetaByIndex: Map<number, DeviceLabel>;
  loadingLabels: boolean;
}

const pageSize = 100;

const formatDisplayTimestamp = (ts: string) => {
  const d = new Date(ts);
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const toLocalISOString = (d: Date) => {
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

export function DeviceTableTab({
  deviceId,
  deviceName,
  labels,
  labelMetaByIndex,
  loadingLabels,
}: DeviceTableTabProps) {
  const [selectedCols, setSelectedCols] = useState<number[]>([]);
  const [daySelected, setDaySelected] = useState<Date | null>(null);

  const [pagedData, setPagedData] = useState<DeviceDataRow[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [showDayPicker, setShowDayPicker] = useState(false);

  const enabledLabels = useMemo(() => labels.filter((l) => l.is_enabled), [labels]);

  // label header text: "Name (unit)"
  const headerForCol = (colIndex: number) => {
    const meta = labelMetaByIndex.get(colIndex);
    const name = meta?.label ?? `col${colIndex}`;
    const unit = meta?.unit ? ` (${meta.unit})` : "";
    return `${name}${unit}`;
  };

  // ✅ robust value lookup (supports "3", "d3", and label name keys)
  const getRawValue = (row: DeviceDataRow, colIndex: number) => {
    const k1 = colIndex.toString();
    const k2 = `d${colIndex}`;
    const nameKey = labelMetaByIndex.get(colIndex)?.label;

    return (
      row.values?.[k1] ??
      row.values?.[k2] ??
      (nameKey ? row.values?.[nameKey] : undefined)
    );
  };

  // apply multiplier/offset + fixed 3 decimals
  const formatScaledValue = (colIndex: number, raw: number | null | undefined) => {
    if (raw === null || raw === undefined) return "--";
    const meta = labelMetaByIndex.get(colIndex);
    const m = meta?.multiplier ?? 1;
    const o = meta?.offset ?? 0;
    const v = raw * m + o;
    return v.toFixed(3);
  };

  const fetchPagedData = async (
    customStart?: Date | null,
    customEnd?: Date | null,
    customOffset: number = 0
  ) => {
    if (isLoading) return;
    if (selectedCols.length === 0) return;

    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      selectedCols.forEach((col) => params.append("columns", col.toString()));
      if (customStart) params.append("start", toLocalISOString(customStart));
      if (customEnd) params.append("end", toLocalISOString(customEnd));
      params.append("limit", pageSize.toString());
      params.append("offset", customOffset.toString());

      const res = await http.get(`/data/${deviceId}/paged_data?${params.toString()}`);
      const data: DeviceDataRow[] = res.data;

      setPagedData((prev) => (customOffset === 0 ? data : [...prev, ...data]));
      setOffset(customOffset + pageSize);
      setHasMore(data.length === pageSize);
    } finally {
      setIsLoading(false);
    }
  };

  // when date changes, reset pagination
  useEffect(() => {
    setPagedData([]);
    setOffset(0);
    setHasMore(true);
  }, [daySelected]);

  // if device changes, clear selection + data
  useEffect(() => {
    setSelectedCols([]);
    setPagedData([]);
    setOffset(0);
    setHasMore(true);
    setDaySelected(null);
  }, [deviceId]);

  return (
    <>
      <Row className="mb-2">
        <Col>
          <strong>Filter Columns:</strong>
          <div className="d-flex flex-wrap">
            {enabledLabels.map((label) => (
              <Form.Check
                key={label.column_index}
                inline
                label={label.label}
                checked={selectedCols.includes(label.column_index)}
                disabled={loadingLabels}
                onChange={() =>
                  setSelectedCols((prev) =>
                    prev.includes(label.column_index)
                      ? prev.filter((i) => i !== label.column_index)
                      : [...prev, label.column_index]
                  )
                }
              />
            ))}
          </div>
        </Col>
      </Row>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <strong>Device: {deviceName}</strong>

        <div className="d-flex align-items-center gap-2">
          <Button
            variant="outline-secondary"
            size="sm"
            className="d-flex align-items-center gap-2"
            onClick={() => setShowDayPicker(true)}
            title="Click to select date"
          >
            <Calendar />
            {daySelected ? daySelected.toLocaleDateString() : "Select Date"}
          </Button>

          <Button
            variant="primary"
            size="sm"
            disabled={!daySelected || selectedCols.length === 0 || isLoading}
            onClick={() => {
              if (!daySelected) return;
              const from = new Date(daySelected);
              from.setHours(0, 0, 0, 0);
              const to = new Date(daySelected);
              to.setHours(23, 59, 59, 999);

              setPagedData([]);
              setOffset(0);
              setHasMore(true);
              fetchPagedData(from, to, 0);
            }}
          >
            Apply Filter
          </Button>
        </div>
      </div>

      <table className="table table-sm table-bordered">
        <thead>
          <tr>
            <th>Timestamp</th>
            {selectedCols.map((colIndex) => (
              <th key={colIndex}>{headerForCol(colIndex)}</th>
            ))}
          </tr>
        </thead>

        <tbody>
          {pagedData.map((row, idx) => (
            <tr key={idx}>
              <td>{formatDisplayTimestamp(row.timestamp)}</td>
              {selectedCols.map((colIndex) => {
                const raw = getRawValue(row, colIndex);
                return <td key={colIndex}>{formatScaledValue(colIndex, raw)}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {hasMore && daySelected && selectedCols.length > 0 && (
        <div className="text-center">
          <Button
            size="sm"
            variant="outline-primary"
            onClick={() => {
              const from = new Date(daySelected);
              from.setHours(0, 0, 0, 0);
              const to = new Date(daySelected);
              to.setHours(23, 59, 59, 999);
              fetchPagedData(from, to, offset);
            }}
            disabled={isLoading}
          >
            {isLoading ? "Loading..." : "Load More"}
          </Button>
        </div>
      )}

      <Modal show={showDayPicker} onHide={() => setShowDayPicker(false)} centered size="sm">
        <Modal.Body className="text-center">
          <DatePicker
            selected={daySelected}
            onChange={(date) => {
              setShowDayPicker(false);
              setDaySelected(date);
            }}
            inline
          />
        </Modal.Body>
      </Modal>
    </>
  );
}

export default DeviceTableTab;